import { OfficielFlagdag, KalenderBegivenhed, MaerkeDag, FoedselsDag } from '../types';
import { OFFICIELLE_FLAGDAGE } from '../constants/officialFlagDays';

export const DANSKE_MAANEDER = [
  'Januar', 'Februar', 'Marts', 'April', 'Maj', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'December',
];

export const DANSKE_MAANEDER_KORT = [
  'jan.', 'feb.', 'mar.', 'apr.', 'maj', 'jun.',
  'jul.', 'aug.', 'sep.', 'okt.', 'nov.', 'dec.',
];

export const DANSKE_DAGE = [
  'Mandag', 'Tirsdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lørdag', 'Søndag',
];

export const DANSKE_DAGE_KORT = ['Ma', 'Ti', 'On', 'To', 'Fr', 'Lø', 'Sø'];

/**
 * Gauss/Anonymous algorithm to calculate Easter Sunday for a given year.
 */
export function beregnPaaskedag(aar: number): Date {
  const a = aar % 19;
  const b = Math.floor(aar / 100);
  const c = aar % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31); // 1-based
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(aar, month - 1, day);
}

/**
 * Resolve an official flag day to a specific date in a given year.
 */
export function resolverOfficielFlagdag(flagdag: OfficielFlagdag, aar: number): Date {
  if (flagdag.beregning === 'fast') {
    return new Date(aar, (flagdag.maaned! - 1), flagdag.dag!);
  } else {
    // paske_offset
    const paaskedag = beregnPaaskedag(aar);
    const dato = new Date(paaskedag);
    dato.setDate(dato.getDate() + (flagdag.paskeOffset ?? 0));
    return dato;
  }
}

/**
 * Resolve all official flag days for a given year.
 */
export function resolverAlleOfficielle(aar: number): KalenderBegivenhed[] {
  const today = startOfDay(new Date());
  return OFFICIELLE_FLAGDAGE.map((flagdag) => {
    const dato = resolverOfficielFlagdag(flagdag, aar);
    return {
      id: `${flagdag.id}-${aar}`,
      titel: flagdag.titel,
      dato,
      type: 'officiel' as const,
      beskrivelse: flagdag.beskrivelse,
      dageVaek: beregnDageVaek(dato, today),
      kilde: 'officiel' as const,
    };
  });
}

/**
 * Resolve user-created events for a given year.
 */
export function resolverBrugerBegivenheder(
  maerkeDage: MaerkeDag[],
  foedselsDage: FoedselsDag[],
  aar: number
): KalenderBegivenhed[] {
  const today = startOfDay(new Date());
  const begivenheder: KalenderBegivenhed[] = [];

  for (const md of maerkeDage) {
    if (md.gentagelse === 'aarlig') {
      // "MM-DD" format
      const parts = md.dato.split('-');
      const maaned = parseInt(parts[0], 10) - 1;
      const dag = parseInt(parts[1], 10);
      const dato = new Date(aar, maaned, dag);
      begivenheder.push({
        id: `${md.id}-${aar}`,
        titel: md.titel,
        dato,
        type: 'maerkedag',
        beskrivelse: md.beskrivelse,
        vej: md.vej,
        vejnummer: md.vejnummer,
        dageVaek: beregnDageVaek(dato, today),
        kilde: 'bruger',
      });
    } else {
      // "YYYY-MM-DD" format — only for the matching year
      const parts = md.dato.split('-');
      const mdAar = parseInt(parts[0], 10);
      if (mdAar === aar) {
        const maaned = parseInt(parts[1], 10) - 1;
        const dag = parseInt(parts[2], 10);
        const dato = new Date(aar, maaned, dag);
        begivenheder.push({
          id: `${md.id}-${aar}`,
          titel: md.titel,
          dato,
          type: 'maerkedag',
          beskrivelse: md.beskrivelse,
          vej: md.vej,
          vejnummer: md.vejnummer,
          dageVaek: beregnDageVaek(dato, today),
          kilde: 'bruger',
        });
      }
    }
  }

  for (const fd of foedselsDage) {
    // "MM-DD" format
    const parts = fd.dato.split('-');
    const maaned = parseInt(parts[0], 10) - 1;
    const dag = parseInt(parts[1], 10);
    const dato = new Date(aar, maaned, dag);

    let alder: number | undefined;
    if (fd.foedselsaar) {
      alder = aar - fd.foedselsaar;
    }

    begivenheder.push({
      id: `${fd.id}-${aar}`,
      titel: `${fd.navn}${alder !== undefined ? ` fylder ${alder}` : ''}`,
      dato,
      type: 'foedselsdag',
      alder,
      vej: fd.vej,
      vejnummer: fd.vejnummer,
      dageVaek: beregnDageVaek(dato, today),
      kilde: 'bruger',
    });
  }

  return begivenheder;
}

function startOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * Calculate days away from today (0 = today, positive = future, negative = past).
 */
export function beregnDageVaek(dato: Date, fra?: Date): number {
  const today = fra ?? startOfDay(new Date());
  const target = startOfDay(dato);
  const diff = target.getTime() - today.getTime();
  return Math.round(diff / (1000 * 60 * 60 * 24));
}

/**
 * Format a date in Danish long format: "mandag den 5. april 2026"
 */
export function formaterDato(dato: Date): string {
  // getDay() returns 0=Sun, 1=Mon, ... 6=Sat
  // We want Mon=0 ... Sun=6
  const jsDay = dato.getDay(); // 0=Sun
  const danskeIndex = jsDay === 0 ? 6 : jsDay - 1;
  const dagNavn = DANSKE_DAGE[danskeIndex].toLowerCase();
  const dag = dato.getDate();
  const maaned = DANSKE_MAANEDER[dato.getMonth()].toLowerCase();
  const aar = dato.getFullYear();
  return `${dagNavn} den ${dag}. ${maaned} ${aar}`;
}

/**
 * Format a date in short Danish format: "5. apr."
 */
export function formaterKortDato(dato: Date): string {
  const dag = dato.getDate();
  const maaned = DANSKE_MAANEDER_KORT[dato.getMonth()];
  return `${dag}. ${maaned}`;
}

/**
 * Get events for a specific date (matching day, month, year).
 */
export function getBegivenhederForDato(
  begivenheder: KalenderBegivenhed[],
  dato: Date
): KalenderBegivenhed[] {
  return begivenheder.filter(
    (b) =>
      b.dato.getFullYear() === dato.getFullYear() &&
      b.dato.getMonth() === dato.getMonth() &&
      b.dato.getDate() === dato.getDate()
  );
}
